// Local raw-stream endpoint for `appostle ssh-proxy`.
//
// The CLI on the same machine as the daemon needs a duplex byte stream to a
// sibling host's `127.0.0.1:22`. The daemon's main WS surface
// (`websocket-server.ts`) is JSON-RPC / agent-protocol over an encrypted
// channel — wrong shape for raw SSH bytes. So we add a SECOND, dedicated WS
// endpoint at `/local/ws-tcp-forward` whose job is to be a dumb pipe between
// the local CLI and a `dialTcpForward(...)` channel against the sibling.
//
// Trust model:
//   - The daemon's HTTP server is already bound to `127.0.0.1` by default;
//     `/local/ws-tcp-forward` is ONLY accepted on loopback IPs (we double-
//     check `req.socket.remoteAddress` against the IPv4/IPv6 loopback range
//     even though the bind already excludes non-loopback).
//   - No wsToken on this leg — the leg is loopback-only, and the daemon
//     itself authenticates outward to the sibling daemon via the wsToken
//     it mints with its own daemonToken.
//   - This is the same trust pattern that already lets `appostle ls`/`logs`
//     hit the local daemon without a session — local CLI = the user.
//
// Query string: `?hostId=<the-sibling's-serverId>`.
// (We use `hostId` as the query name for symmetry with the public API even
// though the value is in fact a `serverId` — that's what `/me/hosts/:id/offer`
// returns and what the relay routes by.)

import type http from "node:http";
import type { Logger } from "pino";
import type { KeyPair } from "@appostle/relay/e2ee";
import { WebSocketServer, type WebSocket as WsWebSocket } from "ws";

import { createAuthServerLookup, dialTcpForward, type TcpForwardChannel } from "./relay-dialer.js";

const LOCAL_TCP_FORWARD_PATH = "/local/ws-tcp-forward";

const LOOPBACK_PREFIXES = ["127.", "::1", "::ffff:127."];

function isLoopback(remote: string | undefined): boolean {
  if (!remote) return false;
  return LOOPBACK_PREFIXES.some((p) => remote.startsWith(p));
}

export interface LocalTcpForwardListenerArgs {
  httpServer: http.Server;
  logger: Logger;
  /** This daemon's auth-server URL (for offer fetch + wsToken mint). */
  authServerUrl: string;
  /** This daemon's auth-server daemonToken (Bearer used by both upstream calls). */
  daemonToken: string;
  /** This daemon's client-identity keypair (X25519) — pinned by sibling allow-lists. */
  clientIdentity: KeyPair;
  clientIdentityPubkeyB64: string;
}

/**
 * Attach the local tcp-forward listener to an existing HTTP server. Returns a
 * `stop()` handle for clean shutdown.
 *
 * The listener uses `WebSocketServer({ noServer: true })` so it can share the
 * same HTTP port (6767 by default) without taking over the main WS path.
 */
export function attachLocalTcpForwardListener(args: LocalTcpForwardListenerArgs): {
  stop: () => void;
} {
  const log = args.logger.child({ module: "local-tcp-forward-listener" });
  const wss = new WebSocketServer({ noServer: true });

  const upgradeHandler = (
    req: http.IncomingMessage,
    socket: import("node:net").Socket,
    head: Buffer,
  ): void => {
    const urlString = req.url ?? "";
    if (!urlString.startsWith(LOCAL_TCP_FORWARD_PATH)) {
      // Not for us; let other listeners handle it.
      return;
    }
    const remote = req.socket.remoteAddress;
    if (!isLoopback(remote)) {
      log.warn({ remote }, "local_tcp_forward_non_loopback_rejected");
      socket.destroy();
      return;
    }

    // Pull hostId off the query string. We need it before we even open the WS
    // because there's no JSON envelope to ferry it across once we're in raw
    // byte mode.
    const parsed = new URL(urlString, "http://localhost");
    const hostId = parsed.searchParams.get("hostId");
    if (!hostId) {
      log.warn("local_tcp_forward_missing_host_id");
      socket.write("HTTP/1.1 400 Bad Request\r\n\r\n");
      socket.destroy();
      return;
    }

    wss.handleUpgrade(req, socket, head, (ws) => {
      void handleLocalSocket({
        log,
        ws,
        hostId,
        authServerUrl: args.authServerUrl,
        daemonToken: args.daemonToken,
        clientIdentity: args.clientIdentity,
        clientIdentityPubkeyB64: args.clientIdentityPubkeyB64,
      });
    });
  };

  args.httpServer.on("upgrade", upgradeHandler);
  log.info({ path: LOCAL_TCP_FORWARD_PATH }, "local_tcp_forward_listener_attached");

  return {
    stop() {
      args.httpServer.off("upgrade", upgradeHandler);
      wss.close();
    },
  };
}

interface HandleArgs {
  log: Logger;
  ws: WsWebSocket;
  hostId: string;
  authServerUrl: string;
  daemonToken: string;
  clientIdentity: KeyPair;
  clientIdentityPubkeyB64: string;
}

async function handleLocalSocket(args: HandleArgs): Promise<void> {
  const { ws, log, hostId } = args;
  ws.binaryType = "arraybuffer";

  let channel: TcpForwardChannel | null = null;
  let closed = false;

  const teardown = (reason: string) => {
    if (closed) return;
    closed = true;
    log.debug({ reason }, "local_tcp_forward_teardown");
    try {
      channel?.close(1000, reason);
    } catch {
      // ignore
    }
    try {
      ws.close(1000, reason);
    } catch {
      // ignore
    }
  };

  ws.on("close", () => teardown("local_ws_closed"));
  ws.on("error", (err) => {
    log.warn({ err }, "local_tcp_forward_ws_error");
    teardown("local_ws_error");
  });

  // Local → sibling: forward outbound bytes to the encrypted relay channel.
  //
  // The message listener is attached SYNCHRONOUSLY here, before the async dial
  // work below (offer fetch + token mint + relay e2ee handshake) — which takes
  // hundreds of ms to seconds. `ssh` writes its identification banner the
  // instant the ProxyCommand connects, so that first frame arrives on this WS
  // *during* the dial window. If the listener were attached only after the dial
  // resolved (as it used to be), Node's EventEmitter would silently drop every
  // frame that arrived in the meantime — most reliably the SSH banner — and
  // sshd would then reject the banner-less stream ("Bad packet length ...").
  // We therefore buffer early frames in `pendingOutbound` and flush them, in
  // order, once the channel is open.
  const pendingOutbound: ArrayBuffer[] = [];
  const forwardChunk = (chunk: ArrayBuffer) => {
    if (closed) return;
    if (!channel) {
      pendingOutbound.push(chunk);
      return;
    }
    void channel.sendBinary(chunk).catch((err) => {
      log.warn({ err }, "local_tcp_forward_relay_send_failed");
      teardown("relay_send_failed");
    });
  };

  ws.on("message", (raw, isBinary) => {
    if (closed) return;
    if (!isBinary && typeof raw === "string") {
      // Tolerate but don't act on text — the CLI should send binary only.
      return;
    }
    let chunk: ArrayBuffer;
    if (raw instanceof ArrayBuffer) {
      chunk = raw;
    } else if (Buffer.isBuffer(raw)) {
      const out = new ArrayBuffer(raw.byteLength);
      new Uint8Array(out).set(raw);
      chunk = out;
    } else {
      const joined = Buffer.concat(raw as Buffer[]);
      const out = new ArrayBuffer(joined.byteLength);
      new Uint8Array(out).set(joined);
      chunk = out;
    }
    forwardChunk(chunk);
  });

  try {
    const lookup = createAuthServerLookup({
      logger: log,
      authServerUrl: args.authServerUrl,
      daemonToken: args.daemonToken,
    });
    const peer = await lookup.fetchPeerOffer(hostId);
    const { token: wsToken } = await lookup.mintWsToken({
      device: args.clientIdentityPubkeyB64,
      hostServerId: peer.serverId,
    });

    channel = await dialTcpForward({
      logger: log,
      clientIdentity: args.clientIdentity,
      clientIdentityPubkeyB64: args.clientIdentityPubkeyB64,
      // A short random clientId is fine — there is no resume/reattach concept
      // for tcp-forward sessions; each invocation is independent.
      clientId: `ssh-proxy-${Math.random().toString(36).slice(2, 10)}`,
      peer,
      wsToken,
    });

    // Flush any frames that arrived (e.g. the SSH banner) while the dial was in
    // flight, preserving order, then let `forwardChunk` send live.
    const buffered = pendingOutbound.splice(0, pendingOutbound.length);
    for (const chunk of buffered) {
      void channel.sendBinary(chunk).catch((err) => {
        log.warn({ err }, "local_tcp_forward_relay_send_failed");
        teardown("relay_send_failed");
      });
    }

    // Sibling → local: forward inbound bytes to the local WS as binary.
    channel.onData((data) => {
      if (closed) return;
      try {
        // The relay's encrypted channel UTF-8-decodes any payload that happens
        // to be valid UTF-8 and delivers it as a STRING (see relay `crypto.ts`
        // `decrypt`). SSH carries plenty of valid-UTF-8 bytes — the server
        // banner ("SSH-2.0-..."), text inside scp/rsync streams, etc. — so we
        // MUST treat strings and ArrayBuffers identically as raw bytes.
        // Re-encoding a string as UTF-8 recovers the exact original bytes
        // (valid UTF-8 round-trips), so no SSH data is corrupted or dropped.
        const bytes = typeof data === "string" ? Buffer.from(data, "utf8") : Buffer.from(data);
        ws.send(bytes);
      } catch (err) {
        log.warn({ err }, "local_tcp_forward_send_failed");
        teardown("local_send_failed");
      }
    });
    channel.onClose((code, reason) => {
      log.debug({ code, reason }, "local_tcp_forward_remote_closed");
      teardown("remote_closed");
    });
    channel.onError((err) => {
      log.warn({ err }, "local_tcp_forward_remote_error");
      teardown("remote_error");
    });
  } catch (err) {
    log.warn({ err, hostId }, "local_tcp_forward_setup_failed");
    teardown("setup_failed");
  }
}
